import { useState, type FormEvent, type ReactNode } from 'react';
import { Card, ErrorBanner, Loading, Spinner } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { useAction, useAsync } from '../../lib/hooks.js';
import { AdminLayout } from './AdminLayout.js';

interface PlatformCredential {
  id: string;
  provider: string;
  label: string;
  credentialHints: Record<string, string>;
  active: boolean;
  createdAt: string;
}

interface CredentialResponse {
  data: PlatformCredential[];
}

/**
 * The Meeting SDK key pair for the platform's Zoom Marketplace app.
 *
 * Platform-level, not per-tenant: one Marketplace app serves every publisher.
 * The per-tenant Zoom credentials that publishers configure themselves are a
 * different thing entirely — those talk to each publisher's own account, and
 * live under the organization's settings.
 *
 * Deliberately a separate screen from Email rather than a shared one. They look
 * alike but behave differently: an email credential can be proved by sending a
 * message to yourself, whereas this can only be proved by a real learner joining
 * a real meeting on Windows. A "Send a test" button here would be a lie.
 */
export function AdminZoomSdkPage(): ReactNode {
  return (
    <AdminLayout>{(staff) => <ZoomSdk canWrite={staff.role === 'superadmin'} />}</AdminLayout>
  );
}

function ZoomSdk({ canWrite }: { canWrite: boolean }): ReactNode {
  const credentials = useAsync(
    () => api.get<CredentialResponse>('/api/admin/platform-credentials?purpose=zoom_sdk'),
    [],
  );
  const { run, pending, error } = useAction();
  const [showForm, setShowForm] = useState(false);
  const [rotating, setRotating] = useState<string | null>(null);

  if (credentials.loading) return <Loading />;

  const list = credentials.data?.data ?? [];
  const active = list.find((c) => c.active);

  async function activate(id: string): Promise<void> {
    await run(async () => {
      await api.post(`/api/admin/platform-credentials/${id}/activate`);
      credentials.reload();
    });
  }

  async function remove(id: string): Promise<void> {
    await run(async () => {
      await api.delete(`/api/admin/platform-credentials/${id}`);
      credentials.reload();
    });
  }

  return (
    <div className="stack">
      <ErrorBanner error={credentials.error} />
      <ErrorBanner message={error} />

      <div className="banner banner-warning">
        The secret is encrypted before it is stored and cannot be read back through any endpoint —
        only replaced. Keep a copy wherever you normally keep secrets.
      </div>

      {!active && (
        <div className="banner">
          No key pair is active, so every learner joins by opening the Zoom app or a browser rather
          than inside RioPlay. That is a working session, not a broken one — this only adds in-app
          joining where the platform can support it.
        </div>
      )}

      {list.length === 0 ? (
        <Card>
          <div className="empty">
            <h2>No Meeting SDK key pair configured</h2>
            <p className="muted small">
              From your Zoom Marketplace app’s <span className="mono">App Credentials</span> page.
              The Client ID ships inside every app build and is not a secret; the Client Secret
              never leaves this server.
            </p>
          </div>
        </Card>
      ) : (
        list.map((credential) => (
          <Card key={credential.id}>
            <div className="row">
              <div>
                <div className="row" style={{ gap: '0.5rem' }}>
                  <strong>{credential.label}</strong>
                  {credential.active && <span className="badge badge-success">Active</span>}
                </div>
                <p className="small faint" style={{ marginTop: '0.3rem' }}>
                  Added {formatDate(credential.createdAt)}
                </p>
              </div>

              {canWrite && (
                <div className="button-row" style={{ marginLeft: 'auto' }}>
                  {!credential.active && (
                    <button
                      type="button"
                      className="button-secondary"
                      disabled={pending}
                      onClick={() => void activate(credential.id)}
                    >
                      Make active
                    </button>
                  )}
                  <button
                    type="button"
                    className="button-secondary"
                    disabled={pending}
                    onClick={() => setRotating(rotating === credential.id ? null : credential.id)}
                  >
                    Replace
                  </button>
                  {!credential.active && (
                    <button
                      type="button"
                      className="button-danger"
                      disabled={pending}
                      onClick={() => void remove(credential.id)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              )}
            </div>

            <table style={{ marginTop: '0.75rem' }}>
              <tbody>
                {Object.entries(credential.credentialHints).map(([field, hint]) => (
                  <tr key={field}>
                    <td className="muted small">{field}</td>
                    <td className="mono">{hint}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {rotating === credential.id && (
              <KeyPairForm
                credentialId={credential.id}
                onDone={() => {
                  setRotating(null);
                  credentials.reload();
                }}
                onCancel={() => setRotating(null)}
              />
            )}
          </Card>
        ))
      )}

      <Card title="How joining behaves">
        {/*
          Stated here because it is the question this screen raises and the
          answer is not guessable. Each line is a platform limitation, not a
          decision we can revisit by configuring something.
        */}
        <table>
          <tbody>
            <tr>
              <td className="muted small">Android, iOS</td>
              <td className="small">
                Watch an advertisement, then open the Zoom app. The SDK cannot pass a ZAK on
                Android, so a meeting in the publisher’s own Zoom account cannot be joined in-app.
              </td>
            </tr>
            <tr>
              <td className="muted small">Windows</td>
              <td className="small">
                Joins inside the app, no advertisement. The Windows SDK accepts a ZAK on join,
                which is what makes another account’s meeting joinable.
              </td>
            </tr>
            <tr>
              <td className="muted small">macOS</td>
              <td className="small">
                Opens the Zoom app. Zoom publishes no Mac Catalyst binding at all.
              </td>
            </tr>
          </tbody>
        </table>
        <p className="small faint" style={{ marginTop: '0.6rem' }}>
          A ZAK is minted per session from the publisher’s own Zoom credentials, for the host named
          on that session. Without those, Windows joins by URL like everything else.
        </p>
      </Card>

      {canWrite &&
        !showForm &&
        list.length === 0 && (
          <div>
            <button type="button" className="button-primary" onClick={() => setShowForm(true)}>
              Add the key pair
            </button>
          </div>
        )}

      {canWrite && showForm && (
        <Card title="Add the Meeting SDK key pair">
          <KeyPairForm
            onDone={() => {
              setShowForm(false);
              credentials.reload();
            }}
            onCancel={() => setShowForm(false)}
          />
        </Card>
      )}
    </div>
  );
}

/**
 * Used both to add and to replace.
 *
 * Replacing sends the whole pair rather than one field: a half-updated credential
 * set leaves nobody able to say what is currently stored, which is the state you
 * least want while joining is failing.
 */
function KeyPairForm({
  credentialId,
  onDone,
  onCancel,
}: {
  credentialId?: string;
  onDone: () => void;
  onCancel: () => void;
}): ReactNode {
  const [sdkKey, setSdkKey] = useState('');
  const [sdkSecret, setSdkSecret] = useState('');
  const { run, pending, error } = useAction();

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await run(async () => {
      if (credentialId) {
        await api.post(`/api/admin/platform-credentials/${credentialId}/credentials`, {
          credentials: { sdkKey, sdkSecret },
        });
      } else {
        await api.post('/api/admin/platform-credentials', {
          purpose: 'zoom_sdk',
          provider: 'marketplace',
          label: 'Zoom Marketplace app',
          credentials: { sdkKey, sdkSecret },
          activate: true,
        });
      }
      onDone();
    });
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="stack" style={{ marginTop: '0.75rem' }}>
      <ErrorBanner message={error} />

      <div>
        <label htmlFor={`sdkKey-${credentialId ?? 'new'}`}>Client ID (SDK key)</label>
        <input
          id={`sdkKey-${credentialId ?? 'new'}`}
          // Not a password field: this value ships inside every app build, so
          // hiding it here would imply a secrecy it does not have.
          type="text"
          autoComplete="off"
          value={sdkKey}
          onChange={(e) => setSdkKey(e.target.value)}
          required
        />
      </div>

      <div>
        <label htmlFor={`sdkSecret-${credentialId ?? 'new'}`}>Client Secret (SDK secret)</label>
        <input
          id={`sdkSecret-${credentialId ?? 'new'}`}
          type="password"
          autoComplete="off"
          value={sdkSecret}
          onChange={(e) => setSdkSecret(e.target.value)}
          required
        />
        <p className="small faint" style={{ marginTop: '0.3rem' }}>
          Never ship this in an app build. Anything inside a package can be extracted from it.
        </p>
      </div>

      <div className="button-row">
        <button type="submit" className="button-primary" disabled={pending}>
          {pending && <Spinner />}
          {credentialId ? 'Replace key pair' : 'Save key pair'}
        </button>
        <button type="button" className="button-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
